// Per-member branded PDF statement (DAASMIN Family Office).
// Uses jsPDF + autoTable. Page 1 = summary + headline numbers + recent txns,
// auto-spilled to subsequent pages if the txn list is long.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DAASMIN_LOGO_BASE64 } from "../assets/daasmin-logo-base64";
import { fmtINR, fmtUnits, fmtPct, longDate } from "./format";

const BRAND = {
  name: "DAASMIN Family Office",
  primary: [16, 185, 129], // emerald-500
  ink: [15, 23, 42],       // slate-900
  muted: [100, 116, 139],  // slate-500
  rule: [220, 225, 232],
};

function drawHeaderFooter(doc, member, periodLabel) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Top band
  doc.setFillColor(...BRAND.ink);
  doc.rect(0, 0, pageW, 26, "F");

  try {
    doc.addImage(DAASMIN_LOGO_BASE64, "PNG", 14, 5, 16, 16);
  } catch (e) {
    // logo embed fails silently
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text(BRAND.name, 34, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(180, 200, 220);
  doc.text("Member account statement", 34, 18);

  const stamp = new Date().toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  doc.setFontSize(8);
  doc.setTextColor(180, 200, 220);
  doc.text(`Generated ${stamp}`, pageW - 14, 18, { align: "right" });

  // Footer
  const page = doc.internal.getCurrentPageInfo().pageNumber;
  const total = doc.internal.getNumberOfPages();
  doc.setDrawColor(...BRAND.rule);
  doc.setLineWidth(0.3);
  doc.line(14, pageH - 12, pageW - 14, pageH - 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text(
    `DAASMIN Family Office — Confidential · ${member?.name || ""} · ${periodLabel || ""}`,
    14,
    pageH - 6,
  );
  doc.text(`Page ${page} of ${total}`, pageW - 14, pageH - 6, { align: "right" });
}

function kvBlock(doc, x, y, items, width = 86) {
  // Renders a 2-column "Label : Value" stack inside a soft box.
  // items: [["Label", "value"], ...]
  doc.setDrawColor(...BRAND.rule);
  doc.setFillColor(248, 250, 252);
  const rowH = 6.5;
  const padX = 4;
  const padY = 4;
  const h = padY * 2 + items.length * rowH;
  doc.roundedRect(x, y, width, h, 1.5, 1.5, "FD");

  items.forEach((row, i) => {
    const ry = y + padY + (i + 0.7) * rowH;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...BRAND.muted);
    doc.text(String(row[0]), x + padX, ry);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...BRAND.ink);
    doc.text(String(row[1]), x + width - padX, ry, { align: "right" });
  });
  return y + h;
}

/**
 * Build & download a per-member branded PDF statement.
 *
 * @param {object} cfg
 * @param {object} cfg.member         the member record (capital, units, gain, etc.)
 * @param {Array}  cfg.transactions   member's transactions (subs / reds / bonus)
 * @param {object} cfg.summary        portfolio summary (we use currentNAV, lastNavDate)
 * @param {object} cfg.metrics        { xirr, cagr, years, firstDate }
 * @param {string} [cfg.periodLabel]  e.g. "As of 08 Jun 2026"
 * @param {string} [cfg.filename]     output filename (no extension)
 */
export function exportMemberStatement(cfg) {
  const {
    member,
    transactions = [],
    summary = {},
    metrics = {},
    periodLabel,
    filename,
  } = cfg || {};

  if (!member) return;

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const today = periodLabel || `As of ${longDate(new Date().toISOString())}`;

  // -------- Page 1 hero block --------
  doc.setTextColor(...BRAND.ink);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("STATEMENT FOR", 14, 36);

  doc.setFontSize(18);
  doc.text(member.name || "", 14, 44);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text(today, 14, 50);

  // -------- Headline numbers row (Capital / Bonus / Overall) --------
  const overallValue =
    member.overallCurrentValue != null ? member.overallCurrentValue : member.currentValue || 0;
  const overallGain =
    member.overallGainLoss != null ? member.overallGainLoss : member.gainLoss || 0;
  const overallPct =
    member.overallGainPct != null ? member.overallGainPct : member.gainLossPct || 0;
  const hasBonus = (member.bonusUnits || 0) > 0 || (member.bonusCapital || 0) > 0;

  let y = 58;
  // Three side-by-side hero boxes (or two if no bonus)
  const boxes = [];
  boxes.push({
    label: "OVERALL VALUE",
    big: fmtINR(overallValue, { compact: true }),
    sub: `${fmtINR(overallGain)} (${fmtPct(overallPct)})`,
    subColor: overallGain >= 0 ? BRAND.primary : [225, 29, 72],
  });
  boxes.push({
    label: "CAPITAL VALUE",
    big: fmtINR(member.currentValue || 0, { compact: true }),
    sub: `Invested ${fmtINR(member.capitalInvested || 0, { compact: true })}`,
    subColor: BRAND.muted,
  });
  if (hasBonus) {
    boxes.push({
      label: "BONUS VALUE",
      big: fmtINR(member.bonusValue || 0, { compact: true }),
      sub: `${fmtUnits(member.bonusUnits || 0)} units`,
      subColor: [245, 158, 11],
    });
  }

  const boxW = (pageW - 28 - (boxes.length - 1) * 4) / boxes.length;
  const boxH = 24;
  boxes.forEach((b, i) => {
    const x = 14 + i * (boxW + 4);
    doc.setDrawColor(...BRAND.rule);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, y, boxW, boxH, 2, 2, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...BRAND.muted);
    doc.text(b.label, x + 4, y + 6);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...BRAND.ink);
    doc.text(b.big, x + 4, y + 14);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...b.subColor);
    doc.text(b.sub, x + 4, y + 20);
  });
  y += boxH + 8;

  // -------- Two-column KV blocks: Capital details / Returns --------
  const colW = (pageW - 28 - 6) / 2;
  const leftItems = [
    ["Capital invested", fmtINR(member.capitalInvested || 0)],
    ["Capital units", fmtUnits(member.units || 0)],
    ["Avg cost NAV", `INR ${(member.avgNAV || 0).toFixed(4)}`],
    ["Current NAV", `INR ${(summary.currentNAV || 0).toFixed(4)}`],
    ["Holding %", fmtPct(member.holdingPct || 0)],
  ];
  const rightItems = [
    ["XIRR (money-weighted)", metrics.xirr != null ? fmtPct(metrics.xirr) : "—"],
    ["CAGR (capital-base)", metrics.cagr != null ? fmtPct(metrics.cagr) : "—"],
    ["Investment duration", metrics.years ? `${metrics.years} yrs` : "—"],
    ["Since first investment", metrics.firstDate ? longDate(metrics.firstDate) : "—"],
    ["Capital gain / loss", `${fmtINR(member.gainLoss || 0)} (${fmtPct(member.gainLossPct || 0)})`],
  ];

  // Section header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.ink);
  doc.text("Capital position", 14, y);
  doc.text("Performance", 14 + colW + 6, y);
  y += 3;

  const yLeftEnd = kvBlock(doc, 14, y, leftItems, colW);
  const yRightEnd = kvBlock(doc, 14 + colW + 6, y, rightItems, colW);
  y = Math.max(yLeftEnd, yRightEnd) + 6;

  if (hasBonus) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.ink);
    doc.text("Bonus position", 14, y);
    y += 3;
    const bonusItems = [
      ["Bonus capital", fmtINR(member.bonusCapital || 0)],
      ["Bonus units", fmtUnits(member.bonusUnits || 0)],
      ["Bonus value (now)", fmtINR(member.bonusValue || 0)],
      [
        "Bonus gain / loss",
        `${fmtINR(member.bonusGainLoss || 0)} (${fmtPct(member.bonusGainPct || 0)})`,
      ],
    ];
    y = kvBlock(doc, 14, y, bonusItems, pageW - 28) + 6;
  }

  // -------- Transactions table (auto-paginates) --------
  // Sort newest first for readability
  const txs = [...(transactions || [])].sort((a, b) => new Date(b.date) - new Date(a.date));

  const rows = txs.map((t) => {
    const isBonus = t.type === "bonus";
    const isCr = !isBonus && (t.fundsCr || 0) > 0;
    const amount = isBonus ? t.fundsCr : isCr ? t.fundsCr : -(t.fundsDr || 0);
    const units = (t.unitsCr || 0) > 0 ? t.unitsCr : t.unitsDr || 0;
    return [
      longDate(t.date),
      isBonus ? "Bonus" : isCr ? "Subscription" : "Redemption",
      `INR ${Number(t.nav || 0).toFixed(4)}`,
      fmtUnits(units),
      fmtINR(amount),
      (t.details || "").slice(0, 60),
    ];
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.ink);
  doc.text(`Transactions  ·  ${rows.length} record${rows.length === 1 ? "" : "s"}`, 14, y);

  autoTable(doc, {
    startY: y + 3,
    head: [["Date", "Type", "NAV", "Units", "Amount", "Details"]],
    body: rows.length ? rows : [["—", "—", "—", "—", "—", "No transactions yet."]],
    styles: { fontSize: 8.5, cellPadding: 2, overflow: "linebreak" },
    headStyles: {
      fillColor: BRAND.ink,
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
    },
    columnStyles: {
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
    },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { top: 32, left: 14, right: 14, bottom: 18 },
    didDrawPage: () => drawHeaderFooter(doc, member, today),
  });

  // Make sure the header is drawn on the very first page even if the table
  // didn't paginate (autoTable's didDrawPage only fires when it actually draws).
  doc.setPage(1);
  drawHeaderFooter(doc, member, today);

  const t = new Date();
  const stamp = `${t.getFullYear()}${String(t.getMonth() + 1).padStart(2, "0")}${String(t.getDate()).padStart(2, "0")}`;
  const safeName = (member.name || "member").replace(/[^a-z0-9]+/gi, "_").toLowerCase();
  const fname = `${filename || `statement_${safeName}`}_${stamp}.pdf`;
  doc.save(fname);
}
